import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { ChevronRightIcon, FolderIcon } from '@heroicons/react/24/outline';
import { DocumentNode } from '../../../types/documents';
import { documentService } from '../../../services/documentService';
import { useToast } from '../../UI/ToastProvider';

function collectDescendantIds(root: DocumentNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: DocumentNode) => {
    ids.add(n.id);
    (n.children ?? []).forEach(walk);
  };
  walk(root);
  return ids;
}

function folderChildren(node: DocumentNode): DocumentNode[] {
  return (node.children ?? []).filter((c) => c.nodeType === 'folder');
}

export interface MoveDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  treeData: DocumentNode | null;
  nodeToMove: DocumentNode | null;
  onExpandNode?: (node: DocumentNode) => void | Promise<void>;
  onMoved?: () => void;
}

export const MoveDocumentModal: React.FC<MoveDocumentModalProps> = ({
  isOpen,
  onClose,
  treeData,
  nodeToMove,
  onExpandNode,
  onMoved,
}) => {
  const { addToast } = useToast();
  const [selectedDestinationId, setSelectedDestinationId] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const forbiddenIds = useMemo(() => {
    if (!nodeToMove) return new Set<string>();
    return collectDescendantIds(nodeToMove);
  }, [nodeToMove]);

  const resetState = useCallback(() => {
    setSelectedDestinationId(null);
    setExpandedNodes(new Set());
    setLoadingNodes(new Set());
  }, []);

  useEffect(() => {
    if (isOpen && treeData?.id) {
      setExpandedNodes(new Set([treeData.id]));
    }
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, treeData?.id, resetState]);

  const isDestinationInvalid = (folderId: string) => forbiddenIds.has(folderId);

  const toggleNode = async (nodeId: string, node: DocumentNode) => {
    const isCurrentlyExpanded = expandedNodes.has(nodeId);

    if (!isCurrentlyExpanded && node.nodeType === 'folder' && node.countChild > 0) {
      setLoadingNodes((prev) => new Set(prev).add(nodeId));
      setExpandedNodes((prev) => new Set(prev).add(nodeId));

      if (onExpandNode) {
        try {
          await onExpandNode(node);
        } catch {
          setExpandedNodes((prev) => {
            const next = new Set(prev);
            next.delete(nodeId);
            return next;
          });
        } finally {
          setLoadingNodes((prev) => {
            const next = new Set(prev);
            next.delete(nodeId);
            return next;
          });
        }
      } else {
        setLoadingNodes((prev) => {
          const next = new Set(prev);
          next.delete(nodeId);
          return next;
        });
      }
    } else {
      setExpandedNodes((prev) => {
        const next = new Set(prev);
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);
        }
        return next;
      });
    }
  };

  const selectedLabel = useMemo(() => {
    if (!treeData || !selectedDestinationId) return null;
    const findTitle = (n: DocumentNode): string | null => {
      if (n.id === selectedDestinationId) return n.title;
      for (const c of n.children ?? []) {
        const t = findTitle(c);
        if (t) return t;
      }
      return null;
    };
    return findTitle(treeData);
  }, [treeData, selectedDestinationId]);

  const handleConfirmMove = async () => {
    if (!nodeToMove || !selectedDestinationId || submitting) return;
    if (isDestinationInvalid(selectedDestinationId)) return;
    setSubmitting(true);
    try {
      await documentService.moveDocument(nodeToMove.id, selectedDestinationId);
      addToast('Item movido com sucesso.', 'success');
      onMoved?.();
      onClose();
    } catch (error: unknown) {
      const ax = error as { response?: { status?: number; data?: { message?: string } } };
      const status = ax.response?.status;
      const msg = ax.response?.data?.message;
      if (status === 400) {
        addToast(msg || 'Destino inválido ou operação não permitida.', 'error');
      } else if (status === 403) {
        addToast(msg || 'Acesso negado.', 'error');
      } else if (status === 404) {
        addToast(msg || 'Documento ou destino não encontrado.', 'error');
      } else if (status === 409) {
        addToast(msg || 'Já existe um item com este nome no mesmo diretório.', 'error');
      } else if (msg) {
        addToast(msg, 'error');
      } else {
        addToast('Não foi possível mover o item.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderFolderBranch = (node: DocumentNode, level: number): React.ReactNode => {
    if (node.nodeType !== 'folder') return null;

    const invalid = isDestinationInvalid(node.id);
    const isExpanded = expandedNodes.has(node.id);
    const isLoading = loadingNodes.has(node.id);
    const isSelected = selectedDestinationId === node.id;
    const kids = folderChildren(node);
    const showChevron = node.nodeType === 'folder' && node.countChild > 0;

    return (
      <div key={node.id} className="select-none">
        <div
          className={`flex items-center py-1.5 px-2 rounded-md text-sm ${
            invalid ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'
          } ${isSelected && !invalid ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}
          style={{ paddingLeft: `${8 + level * 16}px` }}
          onClick={() => {
            if (invalid) return;
            setSelectedDestinationId(node.id);
          }}
        >
          {showChevron ? (
            <div
              className="mr-1 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                void toggleNode(node.id, node);
              }}
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
              ) : (
                <ChevronRightIcon
                  className={`h-4 w-4 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                />
              )}
            </div>
          ) : (
            <span className="w-5 mr-1 shrink-0 inline-block" />
          )}
          <FolderIcon className="h-5 w-5 mr-2 text-yellow-500 shrink-0" />
          <span className="flex-1 truncate">{node.title}</span>
          {invalid && (
            <span className="text-xs text-gray-400 ml-2 shrink-0" title="Não é possível mover para este local">
              inválido
            </span>
          )}
        </div>
        {isExpanded && kids.map((child) => renderFolderBranch(child, level + 1))}
      </div>
    );
  };

  const confirmDisabled =
    submitting ||
    !selectedDestinationId ||
    (selectedDestinationId ? isDestinationInvalid(selectedDestinationId) : true);

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />

        <div className="relative bg-white rounded-lg max-w-lg w-full p-6 shadow-xl">
          <Dialog.Title className="text-lg font-medium text-gray-900 mb-1">Mover item</Dialog.Title>
          <p className="text-sm text-gray-600 mb-4">
            Escolha a pasta de destino para{' '}
            <span className="font-medium text-gray-800">{nodeToMove?.title ?? '…'}</span>.
            Pastas onde o item não pode ser colocado estão desabilitadas.
          </p>

          <div className="border rounded-md max-h-72 overflow-y-auto mb-4 bg-gray-50/50 p-2">
            {!treeData ? (
              <p className="text-sm text-gray-500 p-2">Nenhuma pasta disponível.</p>
            ) : treeData.nodeType !== 'folder' ? (
              <p className="text-sm text-gray-500 p-2">Estrutura inválida: raiz não é pasta.</p>
            ) : (
              renderFolderBranch(treeData, 0)
            )}
          </div>

          {selectedDestinationId && selectedLabel && (
            <p className="text-sm text-gray-600 mb-4">
              Destino: <span className="font-medium">{selectedLabel}</span>
            </p>
          )}

          <p className="text-xs text-gray-500 mb-4">
            Ao confirmar, o item será movido para a pasta selecionada. Esta ação pode ser revertida apenas
            movendo o item novamente.
          </p>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void handleConfirmMove()}
              disabled={confirmDisabled}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Movendo…' : 'Mover'}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
